import React, { useState, useEffect } from 'react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Table from '../ui/Table';
import FormField from '../ui/FormField';
import Modal from '../ui/Modal';
import DailyAnalyticsCharts from './DailyAnalyticsCharts';
import PagedJobReportsTable from './PagedJobReportsTable';
import PagedJobFilesTable from './PagedJobFilesTable';
import BackfillMonitor from './BackfillMonitor';
import { 
  TrendingUp, TrendingDown, Users, FileText, 
  Target, CreditCard, DollarSign, Wallet, BarChart3 
} from 'lucide-react';
import PagedPaymentsTable from './PagedPaymentsTable';
import DailyPaymentsChart from './DailyPaymentsChart';
import PlanUserPurchasesTable from './PlanUserPurchasesTable';
import UserSpendTable from './UserSpendTable';

const ReportsTab = ({ mode = 'superadmin' }) => {
  const [reportCards, setReportCards] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [userSummary, setUserSummary] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
  
  // Date range for user summary (reuse 7d default)
  const [startDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split('T')[0];
  });
  const [endDate] = useState(() => new Date().toISOString().split('T')[0]);

  // Fetch report cards data
  const fetchReportCards = async () => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      if (!adminToken) return;
      const response = await fetch(`${API_BASE_URL}/api/admin/reports/cards`, { headers: { 'Authorization': `Bearer ${adminToken}` } });
      if (response.ok) {
        const data = await response.json();
        setReportCards(data);
      }
    } catch (error) {}
  };

  // Fetch users for dropdown
  const fetchUsers = async () => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      if (!adminToken) return;
      const response = await fetch(`${API_BASE_URL}/api/admin/users`, { headers: { 'Authorization': `Bearer ${adminToken}` } });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {}
  };

  // Fetch user summary
  const fetchUserSummary = async (userId) => {
    if (!userId) return;
    try {
      const adminToken = localStorage.getItem('admin_token');
      if (!adminToken) return;
      const response = await fetch(`${API_BASE_URL}/api/admin/reports/user-summary?user_id=${userId}`, { headers: { 'Authorization': `Bearer ${adminToken}` } });
      if (response.ok) {
        const data = await response.json();
        setUserSummary(data);
      }
    } catch (error) {}
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchReportCards(), fetchUsers()]);
      setLoading(false);
    };
    load();
  }, []);

  useEffect(() => {
    if (selectedUserId) fetchUserSummary(selectedUserId);
  }, [selectedUserId]);

  // Calculate percentage change
  const getPercentageChange = (current, previous) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous * 100).toFixed(1);
  };

  // Format card with trend
  const formatCardValue = (current, previous) => {
    const change = getPercentageChange(current, previous);
    const isPositive = change >= 0;
    return { value: current, change: `${isPositive ? '↑' : '↓'} ${Math.abs(change)}%`, isPositive };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading reports...</div>
      </div>
    );
  }

  const jobsTrend = formatCardValue(reportCards?.jobs_run_today || 0, reportCards?.jobs_run_yesterday || 0);
  const contactsTrend = formatCardValue(reportCards?.total_contacts_scraped_today || 0, reportCards?.total_contacts_scraped_yesterday || 0);
  const apifyUsageYesterday = ((reportCards?.total_contacts_scraped_yesterday || 0) / 1000) * 1.2;
  const profitYesterday = ((reportCards?.total_contacts_scraped_yesterday || 0) / 1000) * 1.3;
  const apifyTrend = formatCardValue(reportCards?.total_apify_usage_usd || 0, apifyUsageYesterday);
  const profitTrend = formatCardValue(reportCards?.total_profit_usd || 0, profitYesterday);

  // Helper to conditionally render sections by mode
  const isPartner = mode === 'partner-admin';

  return (
    <div className="space-y-6">
      {/* Analytics - Only for superadmin */}
      {!isPartner && <DailyAnalyticsCharts />}

      {/* Report Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Jobs Run Today</p>
              <p className="text-2xl font-bold text-gray-900">{jobsTrend.value}</p>
              <p className={`text-sm ${jobsTrend.isPositive ? 'text-green-600' : 'text-red-600'}`}>{jobsTrend.change}</p>
            </div>
            <FileText className="h-8 w-8 text-blue-600" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Contacts Scraped Today</p>
              <p className="text-2xl font-bold text-gray-900">{contactsTrend.value}</p>
              <p className={`text-sm ${contactsTrend.isPositive ? 'text-green-600' : 'text-red-600'}`}>{contactsTrend.change}</p>
            </div>
            <Users className="h-8 w-8 text-green-600" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Users Today</p>
              <p className="text-2xl font-bold text-gray-900">{reportCards?.active_users_today || 0}</p>
            </div>
            <Users className="h-8 w-8 text-purple-600" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Contacts/Job</p>
              <p className="text-2xl font-bold text-gray-900">{reportCards?.average_contacts_per_job || 0}</p>
            </div>
            <Target className="h-8 w-8 text-orange-600" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Success Rate</p>
              <p className="text-2xl font-bold text-gray-900">{reportCards?.success_rate_percentage || 0}%</p>
            </div>
            <TrendingUp className="h-8 w-8 text-green-600" />
          </div>
        </Card>

        {/* APIFY and Profit cards - Only for superadmin */}
        {!isPartner && (
          <>
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">APIFY Usage Today</p>
                  <p className="text-2xl font-bold text-gray-900">${apifyTrend.value}</p>
                  <p className={`text-sm ${apifyTrend.isPositive ? 'text-green-600' : 'text-red-600'}`}>{apifyTrend.change}</p>
                </div>
                <Wallet className="h-8 w-8 text-red-600" />
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Net Profit Today</p>
                  <p className="text-2xl font-bold text-gray-900">${profitTrend.value}</p>
                  <p className={`text-sm ${profitTrend.isPositive ? 'text-green-600' : 'text-red-600'}`}>{profitTrend.change}</p>
                </div>
                <DollarSign className="h-8 w-8 text-green-600" />
              </div>
            </Card>
          </>
        )}
      </div>

      {/* Job Files Backfill Monitor + Table - Only for superadmin */}
      {!isPartner && (
        <>
          <BackfillMonitor />
          <PagedJobFilesTable />
        </>
      )}

      {/* Payments Section */}
      <div className="space-y-6">
        <DailyPaymentsChart />
        <PlanUserPurchasesTable />
        <PagedPaymentsTable />
        <UserSpendTable />
      </div>

      {/* Paged Job Reports */}
      <PagedJobReportsTable />

      {/* User Summary */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">User Summary</h3>
          <div className="w-64">
            <FormField
              type="select"
              label=""
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              options={[{ value: '', label: 'Select a user' }, ...users.map(u => ({ value: u.id, label: `${u.email} - ${u.full_name}` }))]}
            />
          </div>
        </div>

        {userSummary && (
          <div className="overflow-x-auto">
            <Table>
              <thead>
                <tr>
                  <th className="min-w-[120px] text-center">Email</th>
                  <th className="min-w-[100px] text-center">Full Name</th>
                  <th className="min-w-[80px] text-center">Jobs Today</th>
                  <th className="min-w-[90px] text-center">Jobs This Month</th>
                  <th className="min-w-[90px] text-center">Contacts Today</th>
                  <th className="min-w-[100px] text-center">Contacts This Month</th>
                  <th className="min-w-[80px] text-center">Success Rate</th>
                  <th className="min-w-[120px] text-center">Last Job</th>
                  <th className="min-w-[80px] text-center">Plan</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="min-w-[120px] truncate text-center">{userSummary.user_email}</td>
                  <td className="min-w-[100px] truncate text-center">{userSummary.user_full_name}</td>
                  <td className="min-w-[80px] text-center">{userSummary.jobs_run_today}</td>
                  <td className="min-w-[90px] text-center">{userSummary.jobs_run_this_month}</td>
                  <td className="min-w-[90px] text-center">{userSummary.total_contacts_scraped_today}</td>
                  <td className="min-w-[100px] text-center">{userSummary.total_contacts_this_month}</td>
                  <td className="min-w-[80px] text-center">{userSummary.success_rate_percentage}%</td>
                  <td className="min-w-[120px] text-center">{userSummary.last_job_time}</td>
                  <td className="min-w-[80px] truncate text-center">{userSummary.subscription_plan}</td>
                </tr>
              </tbody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ReportsTab;